import React, { useState } from "react";
import { IconCheck } from "@tabler/icons-react";
import "./settings.scss";
import { useClinicData } from "../../context/ClinicDataContext";
import { formatTime, minutesToTime } from "../../utils/format";
import PhoneInput from "../../components/PhoneInput/PhoneInput";

const WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TIMES = Array.from({ length: 29 }, (_, i) => minutesToTime(7 * 60 + i * 30)); // 7:00 AM – 9:00 PM

const SettingsPage = () => {
  const { clinic } = useClinicData();
  const [profile, setProfile] = useState({
    name: clinic.name,
    phone: clinic.phone,
    email: clinic.email,
    address: clinic.address,
  });
  const [hours, setHours] = useState(
    WEEK.map((day) => ({ day, open: clinic.openDays.includes(day), from: clinic.openTime, to: clinic.closeTime }))
  );
  const [rules, setRules] = useState({ slot: clinic.slotMinutes, advance: 60, reminder: 24 });
  const [saved, setSaved] = useState(false);

  const touch = () => setSaved(false);
  const updateProfile = (field) => (e) => {
    setProfile((p) => ({ ...p, [field]: e.target.value }));
    touch();
  };
  const updateHours = (index, changes) => {
    setHours((list) => list.map((h, i) => (i === index ? { ...h, ...changes } : h)));
    touch();
  };
  const updateRules = (field) => (e) => {
    setRules((r) => ({ ...r, [field]: Number(e.target.value) }));
    touch();
  };

  const handleSave = (e) => {
    e.preventDefault();
    // Connect to your API here (see src/api/clinicApi.js).
    setSaved(true);
  };

  return (
    <form className="settings-page" onSubmit={handleSave}>
      <section className="panel">
        <div className="panel__header">
          <div>
            <h2 className="panel__title">Clinic profile</h2>
            <p className="panel__subtitle">Shown on invoices, reminders and the patient app.</p>
          </div>
        </div>
        <div className="form-grid">
          <label className="form-field form-grid__full">
            <span className="form-field__label">Clinic name</span>
            <input className="form-field__control" value={profile.name} onChange={updateProfile("name")} />
          </label>
          <PhoneInput
            label="Phone"
            value={profile.phone}
            hint="Shown on receipts and reminders."
            onChange={(phone) => {
              setProfile((p) => ({ ...p, phone }));
              touch();
            }}
          />
          <label className="form-field">
            <span className="form-field__label">Email</span>
            <input className="form-field__control" type="email" value={profile.email} onChange={updateProfile("email")} />
          </label>
          <label className="form-field form-grid__full">
            <span className="form-field__label">Address</span>
            <textarea className="form-field__control" value={profile.address} onChange={updateProfile("address")} />
          </label>
        </div>
      </section>

      <section className="panel">
        <div className="panel__header">
          <div>
            <h2 className="panel__title">Opening hours</h2>
            <p className="panel__subtitle">Patients can only book inside these hours.</p>
          </div>
        </div>
        <div className="hours-list">
          {hours.map((h, i) => (
            <div className={`hours-list__row ${h.open ? "" : "hours-list__row--closed"}`} key={h.day}>
              <label className="hours-list__toggle">
                <input type="checkbox" checked={h.open} onChange={(e) => updateHours(i, { open: e.target.checked })} />
                <span>{h.day}</span>
              </label>
              {h.open ? (
                <div className="hours-list__times">
                  <label>
                    <span className="visually-hidden">{h.day} opening time</span>
                    <select className="form-field__control" value={h.from} onChange={(e) => updateHours(i, { from: e.target.value })}>
                      {TIMES.map((t) => (
                        <option key={t} value={t}>
                          {formatTime(t)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <span className="hours-list__to">to</span>
                  <label>
                    <span className="visually-hidden">{h.day} closing time</span>
                    <select className="form-field__control" value={h.to} onChange={(e) => updateHours(i, { to: e.target.value })}>
                      {TIMES.filter((t) => t > h.from).map((t) => (
                        <option key={t} value={t}>
                          {formatTime(t)}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              ) : (
                <span className="hours-list__closed">Closed</span>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="panel">
        <div className="panel__header">
          <div>
            <h2 className="panel__title">Booking rules</h2>
            <p className="panel__subtitle">Applies to online bookings from the patient app.</p>
          </div>
        </div>
        <div className="form-grid settings-page__rules">
          <label className="form-field">
            <span className="form-field__label">Time slot length</span>
            <select className="form-field__control" value={rules.slot} onChange={updateRules("slot")}>
              {[15, 30, 60].map((m) => (
                <option key={m} value={m}>
                  {m} minutes
                </option>
              ))}
            </select>
          </label>
          <label className="form-field">
            <span className="form-field__label">Patients can book up to</span>
            <select className="form-field__control" value={rules.advance} onChange={updateRules("advance")}>
              {[14, 30, 60, 90].map((d) => (
                <option key={d} value={d}>
                  {d} days ahead
                </option>
              ))}
            </select>
          </label>
          <label className="form-field">
            <span className="form-field__label">SMS reminder</span>
            <select className="form-field__control" value={rules.reminder} onChange={updateRules("reminder")}>
              {[2, 24, 48].map((h) => (
                <option key={h} value={h}>
                  {h} hours before
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>


      <div className="settings-page__footer">
        {saved && (
          <span className="settings-page__saved" role="status">
            <IconCheck size={18} stroke={2} />
            Changes saved
          </span>
        )}
        <button className="btn btn--primary" type="submit">
          Save changes
        </button>
      </div>
    </form>
  );
};

export default SettingsPage;
